/**
 * The warning tokens a daily result can carry, in plain language.
 *
 * ── WHAT THIS IS AND IS NOT ─────────────────────────────────────────────────
 *
 * The tokens are machine codes minted by `eeg_tracking/config.py` (the `WARN_*`
 * constants), and each was written to contain no substring of that package's
 * `FORBIDDEN_OUTPUT_STRINGS` — no "stress", "fatigue", "clinical", "diagnos",
 * "connectivity" and the rest. **The copy below inherits that obligation.** It
 * says what the condition was, in the same baseline-relative register, and makes
 * no claim the token does not.
 *
 * This is presentation, which §"The rule that governs every screen" permits:
 * turning a documented enum into readable text changes no measurement. What it
 * must never become is interpretation — no token gets copy explaining what it
 * means *about the person*, because that is the package's to say and it does not
 * say it.
 *
 * **An unmapped token is rendered raw, never dropped.** A warning the client does
 * not recognise is still a warning the backend chose to emit, and swallowing it
 * would make a newer analysis version look quieter rather than newer.
 */

/** `eeg_tracking/config.py:478-602`. Ten tokens, and the set is closed. */
export const WARNING_COPY: Readonly<Record<string, string>> = {
  eyes_condition_mismatch:
    'The eyes-open/eyes-closed condition recorded does not match the one this result was scored against.',
  // FIRST-DAY-01 Wording 3. The old sentence said "today is compared against a
  // short history", which is false on day one: there is nothing to compare with.
  insufficient_baseline:
    'Your baseline is still building, so any comparison with it is provisional.',
  low_data: 'This recording had less usable data than usual.',
  muscle_tension_artifact:
    'Muscle tension was elevated in this recording, which affects the fast-band reading in particular.',
  session_rejected: 'This recording did not pass its quality checks, so it was not scored.',
  rest_state_drift:
    'This recording drifted rather than staying steady, which lowers confidence in the day.',
  beta_band_unreliable:
    'The fast-band reading could not be trusted for this recording, so it is not shown.',
  phase_lag_unreliable:
    'The timing-relationship reading could not be trusted for this recording, so it is not shown.',
  preview_not_shippable: 'This result is a preview and is not the recorded outcome.',
}

/**
 * Copy for a terminal recording's `error_code` (backend follow-up §16, ask B7).
 *
 * The SAME idiom as `WARNING_COPY` and for the same reason: the backend owns a
 * closed vocabulary of codes and the client owns the sentence a person reads,
 * with an unmapped code falling through as the raw token rather than as
 * nothing. That fallback is the load-bearing half — a code this build has never
 * seen must still say something true, because "the reason is not recorded" was
 * the defect this field exists to remove.
 *
 * These are the two codes the two terminal write paths can carry today
 * (`brainspot/errors.py`): the cleaning failure and the empty recording.
 */
export const ERROR_CODE_COPY: Readonly<Record<string, string>> = {
  'decoder.cleaning_failed':
    'The recording could not be cleaned well enough to score. That is usually contact or movement during the session rather than anything about you.',
  'recording.empty':
    'The upload carried no signal worth scoring, so nothing was measured.',
}

/**
 * The token acceptance criterion 11 says must never reach a screen.
 *
 * Sprint 1.0.2 took `ec_missing` off the causal path: the backend now passes
 * `paired=False`, and the absence of an eyes-closed pair is disclosed in
 * `validity.alpha_reactivity` as `requires_paired_session` instead. The package
 * still emits the token on the *paired* path, where it is real and actionable —
 * but this product does not use that path.
 *
 * So a payload carrying it is **not a fact about the user's recording**. It says
 * the result was produced by a backend running a pre-0.4.0 `eeg-tracking`. That is
 * an operational anomaly, and dressing it up as a per-day warning would tell a
 * user their recording had a problem when what has a problem is the deployment.
 */
export const BACKEND_VERSION_TOKEN = 'ec_missing'

export interface PresentedWarnings {
  /** Tokens to show the user, in the order the payload listed them. */
  shown: { code: string; copy: string | null }[]
  /**
   * True when `ec_missing` was present. The screen says the result came from an
   * older analysis version; it never renders the token or treats it as a warning
   * about the recording.
   */
  staleAnalysis: boolean
}

export function presentWarnings(codes: readonly string[]): PresentedWarnings {
  const shown = codes
    .filter((code) => code !== BACKEND_VERSION_TOKEN)
    .map((code) => ({ code, copy: WARNING_COPY[code] ?? null }))
  return { shown, staleAnalysis: codes.includes(BACKEND_VERSION_TOKEN) }
}
